import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
} from "@/components/ui";
import { useAppSelector } from "@/config/store";
import { IEdoCourseQuiz, defaultValue } from "@/shared/model/edo-course-quiz.model";
import { IEdoCourse } from "@/shared/model/edo-course.model";
import { IEdoQuiz } from "@/shared/model/edo-quiz.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import { EntityState } from "@/shared/reducers/reducer.utils";
import { Check, Loader2, X } from "lucide-react";
import axios from "axios";
import "moment/locale/it";
import { useEffect, useState } from "react";
import { translate } from "react-jhipster";
import { NumericFormat } from "react-number-format";
import { toast } from "react-toastify";
import { cn } from "@/shared/util/cn";

interface ISendQuizProps {
  quiz?: IEdoQuiz;
  open: boolean;
  onClose: () => void;
  onSuccess?: (courseQuiz: IEdoCourseQuiz) => void;
}

const formatForInput = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const parseFromInput = (value: string): string | null => {
  if (!value) return null;
  return new Date(value).toISOString().slice(0, -1);
};

const SendQuiz = (props: ISendQuizProps) => {
  const { loading, updating, updateSuccess } = useAppSelector<
    EntityState<IEdoCourseQuiz>
  >((state) => state.edoCourseQuiz);
  const [form, setForm] = useState<IEdoCourseQuiz>({ ...defaultValue });
  const [availableCourses, setAvailableCourses] = useState<IEdoCourse[]>([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState<Set<number>>(new Set());
  const { account } = useAppSelector<AuthenticationState>(
    (state) => state.authentication
  );
  const { open, onClose: handleClose, onSuccess: handleSuccess, quiz } = props;

  const isNew = form?.id === null || form?.id === undefined;

  useEffect(() => {
    if (quiz) {
      setForm({ ...defaultValue, quiz });
    }
  }, [quiz]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm({
      ...form,
      [event.target.name]:
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value,
    });
  };

  const handleCloseDialog = () => {
    setForm({ ...defaultValue });
    setSelectedCourseIds(new Set());
    handleClose();
  };

  useEffect(() => {
    if (updateSuccess) {
      handleCloseDialog();
    }
  }, [updateSuccess]);

  useEffect(() => {
    const getTeachersCourses = async () => {
      const response = await axios.get<{ data: IEdoCourse[] }>(
        `/teachers/${account.id}/courses`
      );
      setAvailableCourses(response.data.data);
    };
    if (!open) {
      setForm({ ...defaultValue });
    } else {
      getTeachersCourses();
    }
  }, [open]);

  const saveEntity = async (e: React.FormEvent) => {
    e.preventDefault();
    const selected = availableCourses.filter((c) => selectedCourseIds.has(c.id));
    if (!selected.length) {
      toast.error("Selezionare almeno un corso.");
      return;
    }
    if (isNew && quiz) {
      await Promise.all(
        selected.map((course) =>
          axios.post(`/quizzes/${quiz.id}/course-quizzes`, {
            quiz,
            course,
            availableAttempts: form.availableAttempts,
            duration: form.duration,
            timeOpen: form.timeOpen,
            timeClose: form.timeClose,
            allowReview: form.allowReview,
          })
        )
      );
      toast.success("Quiz inviati con successo.");
      handleCloseDialog();
      handleSuccess?.(null);
    }
  };

  const toggleCourse = (id: number) => {
    setSelectedCourseIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleCloseDialog()}>
      <DialogContent
        showClose={false}
        className="flex max-h-[90vh] max-w-lg flex-col gap-0 overflow-hidden p-0"
      >
        <DialogHeader className="flex shrink-0 flex-row items-center justify-between gap-4 border-b border-[var(--color-mockup-border)] px-6 py-4 dark:border-[var(--color-mockup-border)]">
          <DialogTitle>Invia il test a uno o più corsi</DialogTitle>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handleCloseDialog}
              className="flex h-8 w-8 items-center justify-center rounded-full text-[var(--color-mockup-text-mid)] hover:bg-[var(--color-surface-secondary)] dark:text-[var(--color-mockup-text-light)] dark:hover:bg-primary/15"
              aria-label="Chiudi"
            >
              <X className="h-4 w-4" />
            </button>
            <button
              type="submit"
              form="send-quiz-form"
              disabled={updating || !form.duration || !selectedCourseIds.size}
              className="flex h-8 w-8 items-center justify-center rounded-full text-primary hover:bg-[var(--color-surface-secondary)] disabled:opacity-50 dark:hover:bg-primary/15"
              aria-label="Invia"
            >
              <Check className="h-4 w-4" />
            </button>
          </div>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <form
            id="send-quiz-form"
            onSubmit={saveEntity}
            className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5"
          >
            {isNew && (
              <div>
                <label className="mb-2 block text-sm font-medium text-[var(--color-mockup-text-mid)]">
                  Corsi <span className="text-red-500">*</span>
                </label>
                <div className="max-h-48 space-y-2 overflow-y-auto rounded-2xl border border-[var(--color-mockup-border)] p-2 dark:border-[var(--color-mockup-border)]">
                  {availableCourses.map((course) => (
                    <label
                      key={course.id}
                      className={cn(
                        "flex cursor-pointer items-center gap-3 rounded-2xl px-3 py-2 transition-colors hover:bg-primary/5",
                        selectedCourseIds.has(course.id) && "bg-[var(--color-surface-secondary)]"
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={selectedCourseIds.has(course.id)}
                        onChange={() => toggleCourse(course.id)}
                        className="h-4 w-4 rounded-sm border-[var(--color-mockup-border)] text-primary"
                      />
                      <span className="text-body text-[var(--color-mockup-text)]">
                        {course.subject?.name} - {course.class?.year?.year}
                        {course.class?.section}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <NumericFormat
                onValueChange={(values) =>
                  setForm({ ...form, availableAttempts: values.floatValue })
                }
                value={form.availableAttempts ?? ""}
                allowNegative={false}
                decimalScale={0}
                customInput={Input}
                label={translate("edocendoApp.edoCourseQuiz.availableAttempts")}
                placeholder={translate("edocendoApp.edoCourseQuiz.availableAttempts")}
                name="availableAttempts"
              />
              <NumericFormat
                onValueChange={(values) =>
                  setForm({ ...form, duration: values.floatValue })
                }
                value={form.duration ?? ""}
                allowNegative={false}
                decimalScale={0}
                customInput={Input}
                label={translate("edocendoApp.edoCourseQuiz.duration") + " (min)"}
                placeholder={translate("edocendoApp.edoCourseQuiz.duration")}
                name="duration"
                required
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-[var(--color-mockup-text-mid)]">
                  {translate("edocendoApp.edoCourseQuiz.timeOpen")}
                </label>
                <input
                  type="datetime-local"
                  value={formatForInput(form.timeOpen)}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      timeOpen: parseFromInput(e.target.value),
                    })
                  }
                  min={formatForInput(new Date().toISOString())}
                  className="flex h-10 w-full rounded-md border border-[var(--color-mockup-border)] bg-white px-3 py-2 text-sm dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-[var(--color-mockup-text-mid)]">
                  {translate("edocendoApp.edoCourseQuiz.timeClose")}
                </label>
                <input
                  type="datetime-local"
                  value={formatForInput(form.timeClose)}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      timeClose: parseFromInput(e.target.value),
                    })
                  }
                  disabled={!form.timeOpen}
                  min={form.timeOpen ? formatForInput(form.timeOpen) : undefined}
                  className="flex h-10 w-full rounded-md border border-[var(--color-mockup-border)] bg-white px-3 py-2 text-sm disabled:opacity-50 dark:border-[var(--color-mockup-border)] dark:bg-[var(--color-surface-secondary)]"
                />
              </div>
            </div>
            <label className="flex cursor-pointer items-center gap-3">
              <input
                type="checkbox"
                checked={form.allowReview ?? false}
                onChange={handleChange}
                name="allowReview"
                className="h-4 w-4 rounded-sm border-[var(--color-mockup-border)] text-primary"
              />
              <span className="text-body text-[var(--color-mockup-text)]">
                {translate("edocendoApp.edoCourseQuiz.allowReview")}
              </span>
            </label>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SendQuiz;
